import { Card } from 'components/Card';
import { CardRow } from 'components/CardRow';
import { ICONS } from 'components/Icon';
import { Txt } from 'components/Txt';
import { useSelector } from 'hooks/useSelector';
import { pluralise } from 'lib/pluralise';
import { stringRemoveNewLines } from 'lib/riskbase';
import { theme } from 'lib/theme';
import { union } from 'lodash';
import React from 'react';
import { View } from 'react-native';
import { Chip } from 'screens/AssessmentScreen/Finding/Chip';

import {
  selectRemoteCategories,
  selectRemoteSeverities,
  selectRemoteAseSetupAction,
  selectRemoteTimescales,
} from 'store/remote/selectors';

type Props = {
  id: number;
  onPress: (id: number) => void;
  checked: boolean;
};

export const ActionListItem: React.FC<Props> = React.memo(
  ({ id, onPress, checked }) => {
    const action = useSelector((state) =>
      selectRemoteAseSetupAction(state, id),
    );
    const categories = useSelector(selectRemoteCategories)!;
    const timescales = useSelector(selectRemoteTimescales)!;
    const severities = useSelector(selectRemoteSeverities)!;

    if (!action) {
      return null;
    }

    const countChecklistItems = union(
      action.positiveChecklistItemIds,
      action.negativeChecklistItemIds,
    ).length;
    const countAnswers = action.answerIds.length;

    const severity = severities[action.severityId] ?? severities[0]!;
    const timescale = timescales[action.timescaleId] ?? timescales[0]!;
    const category = categories[action.categoryId] ?? categories[0]!;

    const chips = (category.id !== 0 ||
      severity.id !== 0 ||
      timescale.id !== 0) && (
      <>
        {severity.id !== 0 && (
          <Chip
            iconSource={ICONS.DiamondExclamationSolid}
            color={`#${severity.colour}`}
          >
            {severity.name}
          </Chip>
        )}
        {timescale.id !== 0 && (
          <Chip iconSource={ICONS.HourglassLight}>{timescale.name}</Chip>
        )}
        {category.id !== 0 && (
          <Chip iconSource={ICONS.TagLight}>{category.name}</Chip>
        )}
      </>
    );

    return (
      <Card
        separator={null}
        style={{ marginBottom: theme.paddingVertical / 2 }}
        margin={false}
      >
        <CardRow onPress={() => onPress(id)} checkedValue={checked}>
          <Txt size="small">
            {stringRemoveNewLines(action.what)}
            {!!action.why && (
              <Txt size="small" style={{ color: theme.colors.fieldValueText }}>
                {' '}
                {stringRemoveNewLines(action.why)}
              </Txt>
            )}
          </Txt>
          {(chips || !!countAnswers || !!countChecklistItems) && (
            <View
              style={{
                alignItems: 'center',
                flexDirection: 'row',
                flexWrap: 'wrap',
              }}
            >
              {chips}
              {(!!countAnswers || !!countChecklistItems) && (
                <Txt
                  size="xxxsmall"
                  style={{ color: theme.colors.fieldValueText, marginTop: 6 }}
                >
                  {pluralise(countAnswers, '1 Answer', '{x} Answers', '')}
                  {!!countAnswers && !!countChecklistItems && ' • '}
                  {pluralise(
                    countChecklistItems,
                    '1 Checklist Item',
                    '{x} Checklist Items',
                    '',
                  )}
                </Txt>
              )}
            </View>
          )}
        </CardRow>
      </Card>
    );
  },
);
